import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { WS_URL } from '../config/api';
import { createPresenceRateLimiter } from '../realtime/presenceRateLimiter';
import { buildActivityEntry } from '../realtime/activityFeed';
import { isLiveSocketClose, shouldReconnect, reconnectDelayMs } from '../realtime/socketLifecycle';
import { normalizeCollaborationCursor } from '../realtime/collaborationCursor';

const WebSocketContext = createContext();

const remoteCursorFromPresence = (cursor, presence = {}) => {
  const normalized = normalizeCollaborationCursor(cursor);
  if (!normalized) return null;
  return {
    ...normalized,
    username: presence.username,
    color: presence.color,
    updatedAt: Number(presence.last_updated_at || presence.updatedAt || Date.now())
  };
};

export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }
  return context;
};

export const WebSocketProvider = ({ children }) => {
  const [connected, setConnected] = useState(false);
  const [clientId, setClientId] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [sessionState, setSessionState] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [messages, setMessages] = useState([]);
  const [remoteCursors, setRemoteCursors] = useState({}); // userId -> { x, y, username, color }
  const [remotePlayheads, setRemotePlayheads] = useState({}); // userId -> { positionBeats, isActive, isFollowingTransport, updatedAt, username, color }
  const [ownershipIndicators, setOwnershipIndicators] = useState({
    trackLocks: {},
    clipLocks: {},
    intents: {}
  });
  const [remoteNavigationContexts, setRemoteNavigationContexts] = useState({});
  const [sessionActivity, setSessionActivity] = useState([]);
  const lastMessagesRef = useRef([]);
  const lastSessionStateRef = useRef(null);
  const lastOwnershipRef = useRef({ trackLocks: {}, clipLocks: {}, intents: {} });
  const lastPresenceRef = useRef({});
  const e2eActionProfilesRef = useRef({});
  const [sharedTransportState, setSharedTransportState] = useState({
    isPlaying: false,
    positionBeats: 0,
    bpm: 120,
    loopEnabled: false,
    loopStartBeat: 0,
    loopEndBeat: 16,
    lastController: null,
    lastAction: null,
    updatedAt: null
  });
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const authWaitTimeoutRef = useRef(null);
  const pendingMessagesRef = useRef([]);
  const reconnectAttempts = useRef(0);
  const [isLocalSession, setIsLocalSession] = useState(false);
  const isLocalModeRef = useRef(Boolean(window.__COOKUP_CHAT_PLAYER__)); // Embedded playback never connects or writes.
  const clientEventCounterRef = useRef(0);
  const sessionUpdateListenersRef = useRef(new Set());
  const sessionUpdateAckListenersRef = useRef(new Set());
  const presenceLimiterRef = useRef(createPresenceRateLimiter({
    telemetrySurface: ({ getTelemetrySnapshot, resetTelemetry }) => {
      if (typeof window === 'undefined') return;
      window.__cookupPresenceLimiterTelemetry = {
        getSnapshot: getTelemetrySnapshot,
        reset: resetTelemetry
      };
    }
  }));
  const maxReconnectDelay = 30000; // Max 30 seconds
  const MAX_PENDING_MESSAGES = 200;

  // Synchronous per-message listeners. Consumers that act on messages (the
  // audio engine) subscribe here instead of re-rendering on the history array.
  const messageListenersRef = useRef(new Set());
  const subscribeMessages = useCallback((listener) => {
    if (typeof listener !== 'function') return () => {};
    messageListenersRef.current.add(listener);
    return () => {
      messageListenersRef.current.delete(listener);
    };
  }, []);

  // Helper to limit message history and prevent memory leaks
  const addMessage = useCallback((newMessage) => {
    messageListenersRef.current.forEach((listener) => {
      try {
        listener(newMessage);
      } catch (error) {
        console.error('WebSocket message listener failed', error);
      }
    });
    setMessages(prev => {
      const updated = [...prev, newMessage];
      // Keep a larger bounded history and never drop intermediate session updates.
      // Dropping to "last session_update" causes lossy state under burst traffic.
      return updated.slice(-500);
    });

    const activity = buildActivityEntry(newMessage);
    if (activity) {
      setSessionActivity(prev => [...prev, activity].slice(-40));
    }
  }, []);

  const handleMessage = useCallback((data) => {
    const { type, payload } = data;

    const traceId = payload?.__e2eTrace?.actionId;
    if (traceId) {
      const profile = e2eActionProfilesRef.current[traceId] || { actionId: traceId, stages: {} };
      profile.stages.frontend_message_received = Date.now();
      profile.lastType = type;
      e2eActionProfilesRef.current[traceId] = profile;
    }

    switch (type) {
      case 'connected':
        setClientId(payload?.clientId || null);
        break;

      case 'session_joined':
        // Use colors assigned by backend
        const existingParticipants = (payload.participants || []).map(p => ({
          ...p,
          color: p.color // Use backend-assigned color
        }));
        setParticipants(existingParticipants);
        setSessionState(payload?.state || null);
        if (payload?.state?.transport_state) {
          setSharedTransportState((prev) => ({
            ...prev,
            ...payload.state.transport_state,
            updatedAt: Date.now()
          }));
        }
        if (payload?.ownership_state_snapshot) {
          setOwnershipIndicators(prev => ({
            ...prev,
            trackLocks: payload.ownership_state_snapshot.track_locks || {},
            clipLocks: payload.ownership_state_snapshot.clip_locks || {}
          }));
        }
        if (payload?.presence_state_snapshot?.users) {
          const mapped = {};
          const playheads = {};
          const navigation = {};
          Object.entries(payload.presence_state_snapshot.users).forEach(([userId, p]) => {
            const remoteCursor = remoteCursorFromPresence(p?.cursor, p);
            if (remoteCursor) mapped[userId] = remoteCursor;
            if (Number.isFinite(p?.playhead?.positionBeats)) {
              playheads[userId] = {
                positionBeats: Number(p.playhead.positionBeats),
                isActive: p.playhead.isActive !== false,
                isFollowingTransport: Boolean(p.playhead.isFollowingTransport),
                updatedAt: Number(p.playhead.updatedAt || p.last_updated_at || Date.now()),
                username: p.username,
                color: p.color
              };
            }
            if (p?.navigation) {
              navigation[userId] = {
                ...p.navigation,
                updatedAt: Number(p.navigation.updatedAt || p.last_updated_at || Date.now()),
                username: p.username,
                color: p.color
              };
            }
          });
          setRemoteCursors(mapped);
          setRemotePlayheads(playheads);
          setRemoteNavigationContexts(navigation);
        }
        break;

      case 'transport_state_snapshot':
        setSharedTransportState((prev) => ({
          ...prev,
          ...(payload || {}),
          updatedAt: Date.now()
        }));
        break;

      case 'transport_state_update':
      case 'transport_update':
        setSharedTransportState((prev) => ({
          ...prev,
          isPlaying: payload?.state?.isPlaying ?? (payload?.action === 'play' ? true : (payload?.action === 'pause' || payload?.action === 'stop' ? false : prev.isPlaying)),
          positionBeats: Number.isFinite(payload?.state?.positionBeats) ? payload.state.positionBeats : (Number.isFinite(payload?.position) ? payload.position : prev.positionBeats),
          bpm: Number.isFinite(payload?.state?.bpm) ? payload.state.bpm : prev.bpm,
          loopEnabled: payload?.state?.loopEnabled ?? prev.loopEnabled,
          loopStartBeat: Number.isFinite(payload?.state?.loopStartBeat) ? payload.state.loopStartBeat : prev.loopStartBeat,
          loopEndBeat: Number.isFinite(payload?.state?.loopEndBeat) ? payload.state.loopEndBeat : prev.loopEndBeat,
          lastController: payload?.controller || payload?.hostId || prev.lastController,
          lastAction: payload?.action || prev.lastAction,
          updatedAt: Date.now()
        }));
        addMessage({ type, payload, timestamp: Date.now() });
        break;

      case 'presence_state_snapshot':
      case 'playhead_presence_snapshot':
        if (payload?.users) {
          const mapped = {};
          const intents = {};
          const playheads = {};
          const navigation = {};
          Object.entries(payload.users).forEach(([userId, p]) => {
            const remoteCursor = remoteCursorFromPresence(p?.cursor, p);
            if (remoteCursor) mapped[userId] = remoteCursor;
            if (Number.isFinite(p?.playhead?.positionBeats)) {
              playheads[userId] = {
                positionBeats: Number(p.playhead.positionBeats),
                isActive: p.playhead.isActive !== false,
                isFollowingTransport: Boolean(p.playhead.isFollowingTransport),
                updatedAt: Number(p.playhead.updatedAt || p.last_updated_at || Date.now()),
                username: p.username,
                color: p.color
              };
            }
            if (p?.selection?.focus_resource) {
              intents[userId] = {
                user_id: userId,
                username: p.username,
                color: p.color,
                object_type: p.selection.focus_resource.objectType,
                object_id: p.selection.focus_resource.objectId,
                track_id: p.selection.focus_resource.trackId || null,
                intent: p.selection.focus_resource.intent || 'editing',
                timestamp: p.last_updated_at || Date.now()
              };
            }
            if (p?.navigation) {
              navigation[userId] = {
                ...p.navigation,
                updatedAt: Number(p.navigation.updatedAt || p.last_updated_at || Date.now()),
                username: p.username,
                color: p.color
              };
            }
          });
          setRemoteCursors(mapped);
          setRemotePlayheads(playheads);
          setRemoteNavigationContexts(navigation);
          setOwnershipIndicators(prev => ({ ...prev, intents }));
        }
        break;

      case 'ownership_state_snapshot':
        setOwnershipIndicators(prev => ({
          ...prev,
          trackLocks: payload?.track_locks || {},
          clipLocks: payload?.clip_locks || {}
        }));
        break;

      case 'user_joined':
        // Use color assigned by backend
        setParticipants(prev => [...prev, {
          id: payload.userId,
          username: payload.username,
          color: payload.color // Use backend-assigned color
        }]);
        addMessage({
          type: 'system',
          message: `${payload.username} joined the session`,
          timestamp: Date.now()
        });
        break;

      case 'user_left':
        setParticipants(prev => prev.filter(p => p.id !== payload.userId));
        setRemoteCursors(prev => {
          const updated = { ...prev };
          delete updated[payload.userId];
          return updated;
        });
        setRemotePlayheads(prev => {
          const updated = { ...prev };
          delete updated[payload.userId];
          return updated;
        });
        setRemoteNavigationContexts(prev => {
          const updated = { ...prev };
          delete updated[payload.userId];
          return updated;
        });
        addMessage({
          type: 'system',
          message: `${payload.username} left the session`,
          timestamp: Date.now()
        });
        break;

      case 'chat_message':
        addMessage({
          type: 'chat',
          username: payload.username,
          message: payload.message,
          timestamp: payload.timestamp
        });
        break;

      case 'session_update':
        sessionUpdateListenersRef.current.forEach((listener) => {
          try {
            listener(payload, data.from, data);
          } catch (error) {
            console.error('Session update listener error:', error);
          }
        });
        addMessage({
          type: 'session_update',
          payload: payload,
          from: data.from,
          seq: Number.isFinite(Number(data.seq)) ? Number(data.seq) : null,
          timestamp: Date.now()
        });
        break;

      case 'session_update_ack':
        sessionUpdateAckListenersRef.current.forEach((listener) => {
          try {
            listener(payload, data);
          } catch (error) {
            console.error('Session update acknowledgement listener error:', error);
          }
        });
        break;

      case 'cursor_update':
        {
          const remoteCursor = remoteCursorFromPresence(payload?.cursor || payload, payload);
          if (remoteCursor) {
            setRemoteCursors(prev => ({
              ...prev,
              [payload.userId]: remoteCursor
            }));
          }
        }
        break;

      case 'presence_update_event':
      case 'playhead_presence_update':
        if (payload?.cursor) {
          const remoteCursor = remoteCursorFromPresence(payload.cursor, payload);
          if (remoteCursor) {
            setRemoteCursors(prev => ({
              ...prev,
              [payload.user_id]: remoteCursor
            }));
          }
        }
        if (Number.isFinite(payload?.playhead?.positionBeats)) {
          setRemotePlayheads(prev => ({
            ...prev,
            [payload.user_id]: {
              positionBeats: Number(payload.playhead.positionBeats),
              isActive: payload.playhead.isActive !== false,
              isFollowingTransport: Boolean(payload.playhead.isFollowingTransport),
              updatedAt: Number(payload.playhead.updatedAt || payload.last_updated_at || Date.now()),
              username: payload.username,
              color: payload.color
            }
          }));
        }
        if (payload?.navigation) {
          setRemoteNavigationContexts(prev => ({
            ...prev,
            [payload.user_id]: {
              ...payload.navigation,
              updatedAt: Number(payload.navigation.updatedAt || payload.last_updated_at || Date.now()),
              username: payload.username,
              color: payload.color
            }
          }));
        }
        break;

      case 'cursor_hide':
        setRemoteCursors(prev => {
          const updated = { ...prev };
          delete updated[payload.userId || payload.user_id];
          return updated;
        });
        break;

      case 'presence_leave_event':
        setRemoteCursors(prev => {
          const updated = { ...prev };
          delete updated[payload.userId || payload.user_id];
          return updated;
        });
        setOwnershipIndicators(prev => {
          const intents = { ...(prev.intents || {}) };
          delete intents[payload.userId || payload.user_id];
          return { ...prev, intents };
        });
        setRemotePlayheads(prev => {
          const next = { ...prev };
          delete next[payload.userId || payload.user_id];
          return next;
        });
        setRemoteNavigationContexts(prev => {
          const next = { ...prev };
          delete next[payload.userId || payload.user_id];
          return next;
        });
        break;

      case 'track_lock_applied': {
        const objectType = payload?.objectType || 'param';
        const objectId = payload?.objectId || payload?.trackId;
        const key = `${objectType}:${objectType === 'clip' ? `${payload?.trackId}:${objectId}` : `${payload?.trackId}:${objectId}`}`;
        const lockMapKey = objectType === 'clip' ? 'clipLocks' : 'trackLocks';
        setOwnershipIndicators(prev => ({
          ...prev,
          [lockMapKey]: {
            ...(prev[lockMapKey] || {}),
            [key]: payload?.lock || null
          }
        }));
        addMessage({ type, payload, timestamp: Date.now() });
        break;
      }

      case 'track_unlock_applied': {
        const objectType = payload?.objectType || 'param';
        const objectId = payload?.objectId || payload?.trackId;
        const key = `${objectType}:${objectType === 'clip' ? `${payload?.trackId}:${objectId}` : `${payload?.trackId}:${objectId}`}`;
        const lockMapKey = objectType === 'clip' ? 'clipLocks' : 'trackLocks';
        setOwnershipIndicators(prev => {
          const next = { ...(prev[lockMapKey] || {}) };
          delete next[key];
          return { ...prev, [lockMapKey]: next };
        });
        addMessage({ type, payload, timestamp: Date.now() });
        break;
      }

      case 'ownership_intent_update':
        setOwnershipIndicators(prev => {
          const nextIntents = { ...(prev.intents || {}) };
          if (payload?.intent === 'none' || !payload?.object_type) delete nextIntents[payload?.user_id];
          else nextIntents[payload.user_id] = payload;
          return { ...prev, intents: nextIntents };
        });
        break;

      case 'track_unlock_denied':
      case 'track_updated':
      case 'track_lock_denied':
      case 'track_update_denied':
      case 'session_update_denied':
      case 'undo_result':
      case 'redo_result':
        addMessage({
          type: type,
          payload: payload,
          timestamp: Date.now()
        });
        break;

      default:
      // noop
    }
  }, [addMessage]);

  useEffect(() => {
    lastMessagesRef.current = messages;
  }, [messages]);

  useEffect(() => {
    lastSessionStateRef.current = sessionState;
  }, [sessionState]);

  useEffect(() => {
    lastOwnershipRef.current = ownershipIndicators;
  }, [ownershipIndicators]);

  useEffect(() => {
    lastPresenceRef.current = remoteNavigationContexts;
  }, [remoteNavigationContexts]);

  const setLocalMode = useCallback((flag) => {
    isLocalModeRef.current = Boolean(flag);
    if (flag) pendingMessagesRef.current = []; // clear dead-letter queue
    setIsLocalSession(Boolean(flag));
  }, []);

  const connect = useCallback((url = WS_URL) => {
    if (isLocalModeRef.current) return; // local sessions never connect to WebSocket
    // Prevent duplicate connections
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      // console.log('WebSocket already connected');
      return;
    }

    // Also check if connecting
    if (wsRef.current?.readyState === WebSocket.CONNECTING) {
      // console.log('WebSocket already connecting, please wait...');
      return;
    }

    try {
      const token = sessionStorage.getItem('token');
      if (!token) {
        // In new-tab auth handoff flow, token may appear a moment later.
        if (authWaitTimeoutRef.current) {
          clearTimeout(authWaitTimeoutRef.current);
        }
        authWaitTimeoutRef.current = setTimeout(() => {
          connect(url);
        }, 400);
        return;
      }
      // The login token travels as a subprotocol, never in the URL where proxies log it.
      const ws = new WebSocket(url, ['cookup.v1', `auth.${token}`]);

      ws.onopen = () => {
        // console.log('WebSocket connected');
        setConnected(true);
        reconnectAttempts.current = 0; // Reset on successful connection
        if (pendingMessagesRef.current.length > 0) {
          const pending = [...pendingMessagesRef.current];
          pendingMessagesRef.current = [];
          pending.forEach((payload) => {
            ws.send(payload);
          });
        }
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          handleMessage(data);
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };

      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
      };

      ws.onclose = () => {
        // Ignore a close from a socket that is no longer the live one. This
        // covers both an explicit disconnect() — which nulls the ref before the
        // close event arrives — and a socket that has already been replaced.
        // Without it, a deliberately closed socket resurrects itself on a timer
        // (disconnect clears the pending timeout *before* this handler runs and
        // schedules a new one), and a stale close wipes connection state that
        // belongs to its successor.
        if (!isLiveSocketClose(wsRef.current, ws)) return;

        setConnected(false);
        setClientId(null);
        setSessionId(null);
        setSessionState(null);
        setOwnershipIndicators({ trackLocks: {}, clipLocks: {}, intents: {} });

        if (!shouldReconnect({ isLocalMode: isLocalModeRef.current })) return;

        const delay = reconnectDelayMs(reconnectAttempts.current, maxReconnectDelay);
        reconnectAttempts.current += 1;

        reconnectTimeoutRef.current = setTimeout(() => {
          connect(url);
        }, delay);
      };

      wsRef.current = ws;
    } catch (error) {
      console.error('Error connecting to WebSocket:', error);
    }
  }, [handleMessage]);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    if (authWaitTimeoutRef.current) {
      clearTimeout(authWaitTimeoutRef.current);
      authWaitTimeoutRef.current = null;
    }

    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    setConnected(false);
    setClientId(null);
    setSessionId(null);
    setSessionState(null);
    setRemotePlayheads({});
    setSharedTransportState({
      isPlaying: false,
      positionBeats: 0,
      bpm: 120,
      loopEnabled: false,
      loopStartBeat: 0,
      loopEndBeat: 16,
      lastController: null,
      lastAction: null,
      updatedAt: null
    });
    setParticipants([]);
  }, []);

  const send = useCallback((type, payload) => {
    if (isLocalModeRef.current) return; // local sessions are silent - no WS messages
    const message = JSON.stringify({ type, payload });
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(message);
    } else {
      // Queue briefly so early session actions (before socket open) are not lost.
      pendingMessagesRef.current.push(message);
      if (pendingMessagesRef.current.length > MAX_PENDING_MESSAGES) {
        pendingMessagesRef.current = pendingMessagesRef.current.slice(-MAX_PENDING_MESSAGES);
      }
      if (wsRef.current?.readyState !== WebSocket.CONNECTING) {
        connect();
      }
    }
  }, [connect]);

  const joinSession = useCallback((sessionId, username) => {
    if (isLocalModeRef.current) return; // local sessions have no WS room
    send('join_session', { sessionId, username });
    setSessionId(sessionId);
  }, [send]);

  const leaveSession = useCallback(() => {
    send('leave_session', {});
    setSessionId(null);
    setParticipants([]);
    setRemoteNavigationContexts({});
    setSessionActivity([]);
  }, [send]);

  const sendChatMessage = useCallback((message, username) => {
    send('chat_message', { message, username });
  }, [send]);

  const sendSessionUpdate = useCallback((updateData) => {
    const payload = updateData && typeof updateData === 'object'
      ? { ...updateData }
      : { value: updateData };
    if (!payload.clientEventId) {
      clientEventCounterRef.current += 1;
      const suffix = clientEventCounterRef.current;
      payload.clientEventId = `${Date.now()}-${suffix}-${clientId || 'anon'}`;
    }
    send('session_update', payload);
  }, [send, clientId]);

  const subscribeSessionUpdates = useCallback((listener) => {
    if (typeof listener !== 'function') {
      return () => {};
    }
    sessionUpdateListenersRef.current.add(listener);
    return () => {
      sessionUpdateListenersRef.current.delete(listener);
    };
  }, []);

  const subscribeSessionUpdateAcks = useCallback((listener) => {
    if (typeof listener !== 'function') {
      return () => {};
    }
    sessionUpdateAckListenersRef.current.add(listener);
    return () => {
      sessionUpdateAckListenersRef.current.delete(listener);
    };
  }, []);

  const inferPresenceChannel = useCallback((presencePayload = {}) => {
    if (presencePayload?.navigation) return 'navigation';
    if (presencePayload?.selection?.focus_resource?.intent) return 'intent';
    if (presencePayload?.selection) return 'selection';
    if (presencePayload?.playhead) return 'playhead';
    return 'selection';
  }, []);

  const signatureForPresencePayload = useCallback((presencePayload = {}, channel = 'selection') => {
    if (channel === 'playhead' && presencePayload?.playhead) {
      const playhead = presencePayload.playhead;
      return [
        Number(playhead.positionBeats || 0).toFixed(3),
        playhead.isActive !== false ? '1' : '0',
        playhead.isFollowingTransport ? '1' : '0'
      ].join('|');
    }

    if ((channel === 'selection' || channel === 'intent') && presencePayload?.selection?.focus_resource) {
      const focus = presencePayload.selection.focus_resource;
      return [
        focus.objectType || '',
        focus.objectId || '',
        focus.trackId || '',
        focus.intent || ''
      ].join('|');
    }

    if (channel === 'navigation' && presencePayload?.navigation) {
      const navigation = presencePayload.navigation;
      return [
        Math.round(Number(navigation.scrollLeft || 0)),
        Math.round(Number(navigation.scrollTop || 0)),
        Number(navigation.zoom || 0).toFixed(2),
        navigation.focusTrackId || '',
        navigation.focusClipId || ''
      ].join('|');
    }

    try {
      return JSON.stringify(presencePayload);
    } catch (_) {
      return String(presencePayload);
    }
  }, []);

  const sendCursorUpdate = useCallback((cursorOrX, y) => {
    const payload = cursorOrX && typeof cursorOrX === 'object'
      ? cursorOrX
      : { x: cursorOrX, y };
    const normalized = normalizeCollaborationCursor(payload);
    if (!normalized) return;
    const signature = normalized.surface === 'timeline'
      ? [
        'timeline',
        normalized.timelinePosition.toFixed(4),
        normalized.trackId || '',
        normalized.trackIndex ?? '',
        normalized.laneOffset.toFixed(3)
      ].join('|')
      : (normalized.surface === 'workspace'
        ? `workspace|${normalized.xRatio.toFixed(4)}|${normalized.yRatio.toFixed(4)}`
        : `legacy|${Math.round(normalized.x)}|${Math.round(normalized.y)}`);
    presenceLimiterRef.current?.schedule(
      'cursor',
      payload,
      (nextPayload) => send('presence_update', {
        cursor_position: nextPayload,
        timestamp: Date.now()
      }),
      { signature }
    );
  }, [send]);

  const sendCursorLeave = useCallback(() => {
    presenceLimiterRef.current?.reset('cursor');
    send('cursor_leave', {});
  }, [send]);

  const sendPresenceUpdate = useCallback((presencePayload = {}, options = {}) => {
    const channel = options.channel || inferPresenceChannel(presencePayload);
    const signature = options.signature || signatureForPresencePayload(presencePayload, channel);

    presenceLimiterRef.current?.schedule(
      channel,
      presencePayload,
      (nextPayload) => send('presence_update', nextPayload),
      { force: options.force === true, signature }
    );
  }, [send, inferPresenceChannel, signatureForPresencePayload]);

  const sendPresenceHeartbeat = useCallback(() => {
    send('presence_heartbeat', { timestamp: Date.now() });
  }, [send]);

  const sendNavigationContextUpdate = useCallback((navigation = {}) => {
    sendPresenceUpdate({
      navigation: {
        scrollLeft: Number.isFinite(navigation.scrollLeft) ? Number(navigation.scrollLeft) : 0,
        scrollTop: Number.isFinite(navigation.scrollTop) ? Number(navigation.scrollTop) : 0,
        zoom: Number.isFinite(navigation.zoom) ? Number(navigation.zoom) : 0,
        focusTrackId: navigation.focusTrackId || null,
        focusClipId: navigation.focusClipId || null,
        updatedAt: Date.now()
      },
      timestamp: Date.now()
    }, { channel: 'navigation' });
  }, [sendPresenceUpdate]);

  const sendUndoRequest = useCallback((metadata = {}) => {
    send('undo_request', { timestamp: Date.now(), ...metadata });
  }, [send]);

  const sendRedoRequest = useCallback((metadata = {}) => {
    send('redo_request', { timestamp: Date.now(), ...metadata });
  }, [send]);

  // Track lock/unlock for collaborative mixing
  const sendTrackLock = useCallback((sessionId, trackId, lockTarget = null) => {
    send('track_lock', {
      sessionId,
      trackId,
      ...(lockTarget && typeof lockTarget === 'object' ? lockTarget : {})
    });
  }, [send]);

  const sendTrackUnlock = useCallback((sessionId, trackId, lockTarget = null) => {
    send('track_unlock', {
      sessionId,
      trackId,
      ...(lockTarget && typeof lockTarget === 'object' ? lockTarget : {})
    });
  }, [send]);

  const sendTransportControl = useCallback((action, position, metadata = {}) => {
    send('transport_control', {
      action,
      position,
      ...metadata
    });
  }, [send]);

  const sendTrackUpdate = useCallback((sessionId, trackId, patch, options = null) => {
    send('track_update', {
      sessionId,
      trackId,
      patch,
      ...(options && typeof options === 'object' ? options : {})
    });
  }, [send]);

  const requestObjectLock = useCallback((params = {}) => {
    const {
      sessionId,
      trackId,
      objectType,
      objectId,
      requestId
    } = params;
    if (!sessionId || !trackId || !objectType || !objectId) return;
    sendTrackLock(sessionId, trackId, { objectType, objectId, requestId });
  }, [sendTrackLock]);

  const refreshObjectLock = useCallback((params = {}) => {
    const {
      sessionId,
      trackId,
      objectType,
      objectId,
      requestId
    } = params;
    if (!sessionId || !trackId || !objectType || !objectId) return;
    // Re-using lock acquire path refreshes timestamp for same owner.
    sendTrackLock(sessionId, trackId, { objectType, objectId, requestId });
  }, [sendTrackLock]);

  const releaseObjectLock = useCallback((params = {}) => {
    const {
      sessionId,
      trackId,
      objectType,
      objectId,
      lockToken,
      requestId
    } = params;
    if (!sessionId || !trackId || !objectType || !objectId) return;
    sendTrackUnlock(sessionId, trackId, { objectType, objectId, renew_token: lockToken, requestId });
  }, [sendTrackUnlock]);

  useEffect(() => {
    if (!connected || !sessionId) return undefined;
    const interval = setInterval(() => {
      sendPresenceHeartbeat();
    }, 15000);
    return () => clearInterval(interval);
  }, [connected, sessionId, sendPresenceHeartbeat]);

  useEffect(() => {
    const limiter = presenceLimiterRef.current;
    return () => {
      limiter?.destroy?.();
      if (typeof window !== 'undefined' && window.__cookupPresenceLimiterTelemetry) {
        delete window.__cookupPresenceLimiterTelemetry;
      }
      disconnect();
    };
  }, [disconnect]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const enabled = String(process.env.REACT_APP_ENABLE_E2E_BRIDGE || '').toLowerCase() === 'true';
    if (!enabled) return undefined;

    window.__cookupE2E = {
      createActionProfile: (actionName) => {
        const actionId = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        e2eActionProfilesRef.current[actionId] = {
          actionId,
          actionName: actionName || 'unknown',
          createdAt: Date.now(),
          stages: { client_dispatch_start: Date.now() }
        };
        return actionId;
      },
      markActionStage: (actionId, stage) => {
        if (!actionId || !stage) return;
        const profile = e2eActionProfilesRef.current[actionId] || { actionId, stages: {} };
        profile.stages[stage] = Date.now();
        e2eActionProfilesRef.current[actionId] = profile;
      },
      getActionProfile: (actionId) => e2eActionProfilesRef.current[actionId] || null,
      sendSessionUpdate: (payload) => {
        const actionId = payload?.__e2eTrace?.actionId;
        if (actionId) {
          const profile = e2eActionProfilesRef.current[actionId] || { actionId, stages: {} };
          profile.stages.client_send_session_update = Date.now();
          e2eActionProfilesRef.current[actionId] = profile;
        }
        sendSessionUpdate(payload);
      },
      sendTransportControl: (action, position, trace) => {
        const actionId = trace?.actionId;
        if (actionId) {
          const profile = e2eActionProfilesRef.current[actionId] || { actionId, stages: {} };
          profile.stages.client_send_transport = Date.now();
          e2eActionProfilesRef.current[actionId] = profile;
        }
        sendTransportControl(action, position, trace);
      },
      sendTrackLock: (sessionCode, trackId, options) => sendTrackLock(sessionCode, trackId, options),
      sendTrackUnlock: (sessionCode, trackId, options) => sendTrackUnlock(sessionCode, trackId, options),
      sendUndoRequest: (trace) => {
        const actionId = trace?.actionId;
        if (actionId) {
          const profile = e2eActionProfilesRef.current[actionId] || { actionId, stages: {} };
          profile.stages.client_send_undo = Date.now();
          e2eActionProfilesRef.current[actionId] = profile;
        }
        sendUndoRequest(trace);
      },
      sendRedoRequest: () => sendRedoRequest(),
      sendPresenceUpdate: (payload, options) => {
        const actionId = payload?.__e2eTrace?.actionId;
        if (actionId) {
          const profile = e2eActionProfilesRef.current[actionId] || { actionId, stages: {} };
          profile.stages.client_send_presence = Date.now();
          e2eActionProfilesRef.current[actionId] = profile;
        }
        sendPresenceUpdate(payload, options);
      },
      getRuntimeState: () => ({
        connected,
        sessionId,
        participants,
        remotePlayheads,
        activity: sessionActivity,
        messages: lastMessagesRef.current,
        sessionState: lastSessionStateRef.current,
        ownershipIndicators: lastOwnershipRef.current,
        remoteNavigationContexts: lastPresenceRef.current
      })
    };

    return () => {
      if (window.__cookupE2E) delete window.__cookupE2E;
    };
  }, [sendSessionUpdate, sendTransportControl, sendTrackLock, sendTrackUnlock, sendUndoRequest, sendRedoRequest, sendPresenceUpdate, connected, sessionId, participants, remotePlayheads, sessionActivity]);

  const value = {
    connected,
    clientId,
    sessionId,
    sessionState,
    participants,
    messages,
    sessionActivity,
    remoteCursors,
    remotePlayheads,
    remoteNavigationContexts,
    ownershipIndicators,
    sharedTransportState,
    connect,
    disconnect,
    joinSession,
    leaveSession,
    sendChatMessage,
    sendSessionUpdate,
    subscribeMessages,
    subscribeSessionUpdates,
    subscribeSessionUpdateAcks,
    sendCursorUpdate,
    sendCursorLeave,
    sendPresenceUpdate,
    sendPresenceHeartbeat,
    sendNavigationContextUpdate,
    sendUndoRequest,
    sendRedoRequest,
    sendTransportControl,
    sendTrackLock,
    sendTrackUnlock,
    sendTrackUpdate,
    requestObjectLock,
    refreshObjectLock,
    releaseObjectLock,
    isLocalSession,
    setLocalMode
  };

  return <WebSocketContext.Provider value={value}>{children}</WebSocketContext.Provider>;
};
